import React, { useState, useEffect, useContext } from 'react';
import useIsMobile from '@hooks/useIsMobile';
import useIsBetweenBreakpoints from '@hooks/useIsBetweenBreakpoints';
import RoutingPath from '@constants/routingPath';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
// eslint-disable-next-line import/no-named-as-default
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import Gallery from '@components/HotelCard/Gallery';
import Amenities from '@components/HotelCard/Amenities';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import { useCookies } from 'react-cookie';
import { unionizeFavorites } from '@helpers/addHotelToFavorites';
import Image from 'next/legacy/image';
import grayHeartIcon from '@assets/images/gray-heart-icon.svg';
import redHeartIcon from '@assets/images/red-heart-icon.svg';
import updateHotelFavorites from '@helpers/updateHotelFavorites';
import { srpBadgeHotelCardClicked, SRPHotelCardClick } from '@events/SRP';
import { hotelFavoriteTappedOnSRP, myFavoritesTappedOnList } from '@events/myFavorites';
import _, { isArray, isNull, isUndefined } from 'lodash';
import dateWithinLastNDays from '@helpers/dateWithinLastNDays';
import { useRouter } from 'next/router';
import useFavorites from '@hooks/useFavorites';
import arrowRight from '@assets/images/arrow-right.svg';
import { HotelFavoritesApi } from '@customTypes/hotel-api';
import StarRating from '@components/common/Yotpo/StarRating';

type Props = {
  hotel: HotelFavoritesApi;
  userFavoritesIds?: any;
  setUserFavoritesIds?: any;
};

type Slide = {
  key: string;
  url: string;
  imgClassnames: string;
};

type ImageProps = {
  picture: {
    details: { url: string };
    width_375: { url: string };
    url: string;
  };
};

const defaultProps = {
  userFavoritesIds: [],
  setUserFavoritesIds: () => {},
};

export default function HotelCardFavorite({ hotel, userFavoritesIds, setUserFavoritesIds }: Props) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const minPadding = useIsBetweenBreakpoints(850, 1023);
  const [mobileImages, setMobileImages] = useState<Slide[]>([]);

  const globalState = useContext(store);
  const { dispatch } = globalState;

  const [cookies, setCookie] = useCookies(['userInformation']);
  const { getItem, setItem } = useSessionStorage();
  const userLocalStorageFavorites = getItem(sessionStorageKeys.USER_SESSION_FAVORITES);
  const { addHotelToFavorites } = useFavorites();

  const favorites = unionizeFavorites(
    userLocalStorageFavorites,
    cookies.userInformation?.favorites,
  );

  const [userFavorites, setUserFavorites] = useState<any>(
    userFavoritesIds && userFavoritesIds.length > 0 ? userFavoritesIds : favorites,
  );
  const [showFavoriteToastMessage, setShowFavoriteToastMessage] = useState<boolean>(false);
  const [saveToFavorites, setSaveToFavorites] = useState<boolean>(false);

  const params = router.query;

  useEffect(() => {
    const images = hotel.image.map((image: ImageProps) => ({
      key: image.picture.details.url,
      url: image.picture.details.url,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image.picture.url,
    }));

    if (hotel.image_desktop !== images[0]?.urlShort) {
      const filteredImages = images.filter((item) => item.urlShort !== hotel.image_desktop);
      filteredImages.unshift({
        key: hotel.image_desktop,
        url: hotel.image_desktop,
        imgClassnames: 'w-full swiper-lazy rounded-xl',
        urlShort: hotel.image_desktop,
      });
      setMobileImages(filteredImages);
      return;
    }
    setMobileImages(images);
  }, [hotel.image, hotel.image_desktop, hotel.name]);

  const generateDLPUrl = () => {
    const rootUrl = `${RoutingPath.DLP}/${hotel.url}`;

    // Pick only the URL parameters that start with "utm_" and construct the query string
    const queryString = _.chain(params)
      .pickBy((value, key) => key.startsWith('utm_') && !isUndefined(value) && !isNull(value))
      .map((value, key) => {
        // Handle arrays properly
        if (isArray(value)) {
          return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
        }

        return `${encodeURIComponent(key)}=${encodeURIComponent(value || '')}`;
      })
      .join('&')
      .value();

    return `${rootUrl}${queryString ? `?${queryString}` : ''}`;
  };

  const navigateToDetailsPage = () => {
    if (window) {
      dispatch({
        type: actionTypes.UPDATE_SRP_SCROLL_POSITION,
        payload: window?.scrollY,
      });
      dispatch({
        type: actionTypes.UPDATE_WINDOW_URL,
        payload: window.location.href,
      });
    }

    // Event
    SRPHotelCardClick(hotel.name);
    myFavoritesTappedOnList(+hotel.id, hotel.name);

    // Open DLP in a new tab (it includes utm params)
    window.open(generateDLPUrl(), '_blank');

    // RecentlyAddedBadgeEvent
    if (dateWithinLastNDays(hotel.created_at, 90)) {
      srpBadgeHotelCardClicked();
    }
  };

  const handleAddingHotelIdToFavorites = () => {
    addHotelToFavorites(
      cookies,
      setCookie,
      hotel,
      setShowFavoriteToastMessage,
      userFavorites,
      setSaveToFavorites,
      setItem,
      getItem,
      setUserFavorites,
    );

    if (!userFavorites.includes(hotel.id.toString())) {
      hotelFavoriteTappedOnSRP(+hotel.id, hotel.name);
    }
  };

  useEffect(() => {
    if (setUserFavoritesIds) {
      setUserFavoritesIds(userFavorites);
    }
  }, [setUserFavoritesIds, saveToFavorites, userFavorites]);

  useEffect(() => {
    if (
      userFavorites &&
      !(window.location.href.includes('my-favorites') || window.location.href.includes('bookings'))
    ) {
      updateHotelFavorites(cookies, null, null, getItem, setUserFavorites, null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showFavoriteToastMessage && <FavoritesToastMessage saveToFavorites={saveToFavorites} />}
      <a
        role="button"
        tabIndex={0}
        onClick={(e: any) => {
          e.preventDefault();
          if (!e.target?.alt && e.target?.alt !== 'heart') {
            navigateToDetailsPage();
          }
        }}
        onKeyPress={(e: any) => {
          e.preventDefault();
          if (!e.target?.alt && e.target?.alt !== 'heart') {
            navigateToDetailsPage();
          }
        }}
        className="mb-6 min-w-230 max-w-1024 flex flex-col bg-white outline-none text-rp-primary-black d:cursor-pointer d:shadow-card d:rounded-xl d:flex-row d:w-full"
      >
        <Gallery
          classes={`px-6 ${minPadding ? 'd:w-4/12 cursor-pointer' : 'd:w-5/12 cursor-pointer'}`}
          name={hotel.name}
          mainImage={hotel.image_desktop}
          mobileImages={mobileImages}
          compact={isMobile}
          navigateToDetailsPage={() => {
            if (!isMobile) {
              navigateToDetailsPage();
            }
          }}
          userFavorites={userFavorites}
          handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
          hotel={hotel}
          hotelUrl={`/hotels/${hotel.url}`}
          isUserViewingFavoritesInMyAccount
        />

        <div
          className={`flex-grow w-full relative d:${
            minPadding ? 'px-6' : 'px-8'
          } pt-0 d:pt-10 d:pb-6 ${minPadding ? 'd:w-8/12' : 'd:w-7/12'}`}
        >
          <div
            className="hidden mb-2 d:block text-11 font-rp-pn-semi-bold uppercase text-custom-gray-2 tracking-0.6"
            data-testid="hotel-city-and-state"
          >
            {hotel.code ? `${hotel.city_name}, ${hotel.code}` : `${hotel.city_name}`}
          </div>

          <div
            className={`hidden absolute top-11 cursor-pointer ${
              minPadding ? 'right-6' : 'right-8'
            } d:block text-11 font-rp-pn-semi-bold uppercase text-custom-gray-2 tracking-0.6 d:w-1/4 d:text-right`}
          >
            <Image
              src={
                userFavorites && userFavorites.includes(hotel.id.toString())
                  ? redHeartIcon
                  : grayHeartIcon
              }
              alt="heart"
              onClick={handleAddingHotelIdToFavorites}
            />
          </div>

          <h2
            className="mb-0 w-full text-19 leading-7 font-rp-pn-semi-bold px-6 d:px-0 d:text-22 d:w-3/4 d:-mt-1 d:mb-10px"
            id={hotel.name}
          >
            {hotel.name}
          </h2>

          <div className="flex items-center mt-2 px-6 d:mt-0 d:px-0 d:mb-2.5 mb-2">
            {hotel.avg_rating > 0 ? (
              <StarRating
                algoliaRating={hotel.avg_rating}
                algoliaReviews={hotel.reviews_count}
                hideReviewsNumber
              />
            ) : null}
          </div>

          <div className="overflow-hidden pl-6 d:pl-0 d:overflow-visible relative">
            <Amenities amenities={hotel.amenities} />
          </div>

          <div
            className="mt-0 flex flex-col relative d:flex-row d:mt-8 d:items-center overflow-auto"
            data-testid="hotel-card-products"
          >
            {!isMobile ? (
              <div className="flex justify-end w-full">
                <button
                  data-testid={`hotel-${hotel.id}-card-view-button`}
                  type="button"
                  onClick={navigateToDetailsPage}
                  className="bg-white border border-rp-gray-divider bottom-5 h-9 right-0 rounded-full w-9 flex items-center justify-center"
                >
                  <Image src={arrowRight} alt={`View ${hotel.name}`} width={18} height={18} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </a>
    </>
  );
}

HotelCardFavorite.defaultProps = defaultProps;
